"use strict"

class ProductPage {
  constructor(container) {
    if (!container) return;
    if (container.dataset.productPageInitialized === 'true') return;

    this.container = container;
    this.container.dataset.productPageInitialized = 'true';

    this.variantDataElement = this.container.querySelector('[data-product-variants]');
    this.variantData = this.variantDataElement ? JSON.parse(this.variantDataElement.textContent) : [];

    this.currentVariant = this.getInitialVariant();

    this.init();
  }

  init() {
    this.initGallerySwiper();
    this.initPackPicker();
    this.initAjaxAddToCart();
    this.initQuantitySelector();
    this.initBundleAccordion();
    this.initRecommendationButtons();
    this.initRecommendationsSwiper();
    this.initProductForm();
    this.initSellingPlanWidget();
    this.initPerfectSet();
    this.initPdpTabs();
    this.initSpecsShowMore();
    this.initIncludedProductModal();
    this.initSidebarAtc();
    this.initVariantSelector();
    this.initStickyBar();
  }

  getInitialVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = Number(urlParams.get('variant'));

    if (urlVariantId && this.variantData.length) {
      const matched = this.variantData.find((v) => v.id === urlVariantId);
      if (matched) return matched;
    }

    return this.variantData.find((v) => v.available) || this.variantData[0] || null;
  }

  // ─── SWIPER GALLERY ───────────────────────────────────────────

  initGallerySwiper() {
    if (typeof Swiper === 'undefined') return;

    const mainEl = this.container.querySelector('[data-product-swiper-main]');
    const thumbsEl = this.container.querySelector('[data-product-swiper-thumbs]');

    if (!mainEl) return;

    let thumbsSwiper = null;

    if (thumbsEl) {
      thumbsSwiper = new Swiper(thumbsEl, {
        spaceBetween: 9,
        slidesPerView: 8,
        direction: 'vertical',
        freeMode: true,
        watchSlidesProgress: true,
        breakpoints: {
          0: { direction: 'horizontal', slidesPerView: 6 },
          768: { direction: 'vertical', slidesPerView: 7 },
          1024: { direction: 'vertical', slidesPerView: 8 },
        },
      });
    }

    this.mainSwiper = new Swiper(mainEl, {
      spaceBetween: 0,
      slidesPerView: 1,
      loop: false,
      navigation: {
        prevEl: mainEl.querySelector('[data-swiper-prev]'),
        nextEl: mainEl.querySelector('[data-swiper-next]'),
      },
      pagination: {
        el: mainEl.querySelector('[data-swiper-pagination]'),
        clickable: true,
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
    });

    this.thumbsSwiper = thumbsSwiper;

    if (this.currentVariant && this.currentVariant.featured_media) {
      this.slideToMedia(this.currentVariant.featured_media.id);
    }
  }

  slideToMedia(mediaId) {
    if (!this.mainSwiper) return;

    const slides = this.mainSwiper.slides;
    for (let i = 0; i < slides.length; i++) {
      if (String(slides[i].dataset.mediaId) === String(mediaId)) {
        this.mainSwiper.slideTo(i, 0);
        break;
      }
    }
  }

  // ─── PACK PICKER ──────────────────────────────────────────────

  initPackPicker() {
    this.packOptions = this.container.querySelectorAll('[data-pack-option]');
    this.packRadios = this.container.querySelectorAll('[data-pack-radio]');
    this.selectedLabel = this.container.querySelector('[data-pack-selected-label]');
    this.selectedPrice = this.container.querySelector('[data-pack-selected-price]');
    this.addToCartBtn = this.container.querySelector('[data-pack-add-to-cart]');
    this.buyNowBtn = this.container.querySelector('[data-pack-buy-now]');

    if (!this.packOptions.length) return;

    this.packOptions.forEach((option) => {
      option.addEventListener('click', () => {
        this.selectPackOption(option);
      });
    });
  }

  selectPackOption(option) {
    const pens = Number(option.dataset.pens) || 1;
    const packPrice = Number(option.dataset.packPrice) || 0;

    this.currentPens = pens;

    this.packOptions.forEach((opt) => {
      const isActive = opt === option;
      opt.classList.toggle('pack-picker__option--active', isActive);
      const radio = opt.querySelector('[data-pack-radio]');
      if (radio) radio.checked = isActive;
    });

    const pensWord = pens === 1 ? 'pen' : 'pens';

    if (this.selectedLabel) {
      this.selectedLabel.textContent = pens + ' ' + pensWord;
    }
    if (this.selectedPrice) {
      this.selectedPrice.textContent = option.dataset.priceDisplay || this.formatMoney(packPrice);
    }
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.pens = pens;
    }
    if (this.buyNowBtn) {
      const variantId = this.addToCartBtn ? this.addToCartBtn.dataset.variantId : '';
      this.buyNowBtn.href = '/cart/' + variantId + ':' + pens + '?checkout';
    }
  }

  // ─── ADD TO CART ──────────────────────────────────────────────

  initAjaxAddToCart() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener('click', async () => {
      const variantId = Number(this.addToCartBtn.dataset.variantId);
      const pens = Number(this.addToCartBtn.dataset.pens) || 1;
      if (!variantId) return;

      const initialText = this.addToCartBtn.textContent;
      try {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = 'Adding...';
        await addCartItem(variantId, pens);
        refreshCartUi({ openDrawer: true });
      } catch (error) {
        console.error('Pack picker add to cart error:', error);
      } finally {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = initialText;
      }
    });
  }

  // ─── QUANTITY SELECTOR ───────────────────────────────────────

  initQuantitySelector() {
    const selector = this.container.querySelector('[data-quantity-selector]');
    if (!selector) return;

    const input = selector.querySelector('[data-quantity-input]');
    const minusBtn = selector.querySelector('[data-quantity-minus]');
    const plusBtn = selector.querySelector('[data-quantity-plus]');

    if (!input) return;

    const updateMinusState = () => {
      if (minusBtn) {
        const val = parseInt(input.value, 10) || 1;
        minusBtn.disabled = val <= 1;
      }
    };

    const onQuantityChange = () => {
      updateMinusState();
      this.updateAtcPrice();
    };

    updateMinusState();

    if (minusBtn) {
      minusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        if (current > 1) input.value = current - 1;
        onQuantityChange();
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || 1;
        input.value = current + 1;
        onQuantityChange();
      });
    }

    input.addEventListener('change', onQuantityChange);
    input.addEventListener('input', onQuantityChange);
  }

  updateAtcPrice() {
    const addBtn = this.container.querySelector('[data-add-to-cart]');
    if (!addBtn || addBtn.classList.contains('klaviyo-bis-trigger')) return;

    const priceEl = addBtn.querySelector('[data-atc-price]');
    if (!priceEl) return;

    const quantityInput = this.container.querySelector('[data-quantity-input]');
    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
    const unitPrice = this.getCurrentUnitPrice();
    const totalPrice = unitPrice * quantity;

    addBtn.dataset.unitPrice = String(unitPrice);
    priceEl.textContent = this.formatMoney(totalPrice);
    this.syncPricingDisplay(unitPrice, quantity, totalPrice);
  }

  getCurrentUnitPrice() {
    const addBtn = this.container.querySelector('[data-add-to-cart]');
    let unitPrice = this.currentVariant?.price;
    if (unitPrice == null) {
      unitPrice = Number(addBtn?.dataset.unitPrice) || 0;
    }

    const sellingPlanInput = this.container.querySelector('[data-selling-plan-input]');
    const select = this.container.querySelector('[data-selling-plan-select]');
    if (sellingPlanInput?.value && select?.options?.[select.selectedIndex]) {
      const option = select.options[select.selectedIndex];
      const adjusted = this.getPlanAdjustedPrice(unitPrice, option);
      if (adjusted != null) unitPrice = adjusted;
    }

    return unitPrice;
  }

  getPlanAdjustedPrice(basePrice, option) {
    if (!option) return null;
    const adjType = option.dataset.planAdjType || '';
    const adjValue = Number(option.dataset.planAdjValue);

    if (adjType === 'percentage' && adjValue > 0) {
      return Math.round(basePrice * (100 - adjValue) / 100);
    }
    if (adjType === 'fixed_amount' && adjValue > 0) {
      return Math.max(0, basePrice - adjValue);
    }
    if (adjType === 'price' && adjValue > 0) {
      return adjValue;
    }

    const planPrice = Number(option.dataset.planPrice);
    return planPrice > 0 ? planPrice : null;
  }

  syncPricingDisplay(unitPrice, quantity, totalPrice) {
    const productPriceEls = this.container.querySelectorAll('[data-product-price]');
    productPriceEls.forEach((el) => {
      el.textContent = this.formatMoney(totalPrice);
    });

    const compareValueEl = this.container.querySelector('.product-page__pricing-compare-value');
    const savingsEl = this.container.querySelector('[data-bundle-savings]');
    const pricingCompare = this.container.querySelector('.product-page__pricing-compare');
    const pricingMain = this.container.querySelector('.product-page__pricing-main');

    if (savingsEl || compareValueEl) {
      let separatelyUnit = Number(savingsEl?.dataset.separatelyPrice) || 0;
      const variant = this.currentVariant;
      if (variant?.compare_at_price && variant.compare_at_price > variant.price) {
        separatelyUnit = variant.compare_at_price;
      }

      if (separatelyUnit > 0 && separatelyUnit > unitPrice) {
        const separatelyTotal = separatelyUnit * quantity;
        const savingsAmount = separatelyTotal - totalPrice;
        const savingsPercent = Math.round((savingsAmount / separatelyTotal) * 100);

        if (compareValueEl) compareValueEl.textContent = this.formatMoney(separatelyTotal);
        if (pricingCompare) pricingCompare.style.display = '';
        if (pricingMain) pricingMain.style.display = '';

        if (savingsEl) {
          savingsEl.dataset.separatelyPrice = String(separatelyUnit);
          const textEl = savingsEl.querySelector('[data-bundle-savings-text]');
          if (textEl) {
            textEl.textContent =
              'You save ' + this.formatMoney(savingsAmount) + ' \u2014 ' + savingsPercent + '% off';
          }
          savingsEl.style.display = '';
        }
      } else if (pricingCompare || pricingMain || savingsEl) {
        // Keep static bundle UI if no live compare; only hide when we know there's no savings
        if (separatelyUnit > 0 && separatelyUnit <= unitPrice) {
          if (pricingCompare) pricingCompare.style.display = 'none';
          if (pricingMain) pricingMain.style.display = 'none';
          if (savingsEl) savingsEl.style.display = 'none';
        }
      }
    }

    const stickyBar = document.querySelector('[data-pdp-sticky-bar]');
    if (stickyBar) {
      const stickyPrice = stickyBar.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.formatMoney(totalPrice);
    }
  }

  // ─── BUNDLE ACCORDION ──────────────────────────────────────

  initBundleAccordion() {
    const trigger = this.container.querySelector('[data-bundle-trigger]');
    const content = this.container.querySelector('[data-bundle-content]');
    if (!trigger || !content) return;

    // Start closed on mobile
    if (window.matchMedia('(max-width: 768px)').matches) {
      trigger.setAttribute('aria-expanded', 'false');
      content.hidden = true;
      trigger.closest('[data-bundle-accordion]')?.classList.add('is-collapsed');
    }

    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      content.hidden = expanded;
      trigger.closest('[data-bundle-accordion]')?.classList.toggle('is-collapsed', expanded);
    });
  }

  // ─── RECOMMENDATION BUTTONS ────────────────────────────────

  initRecommendationButtons() {
    const buttons = this.container.querySelectorAll('[data-recommendation-add]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener('click', async () => {
        const variantId = Number(button.dataset.variantId);
        if (!variantId) return;

        button.disabled = true;
        try {
          await addCartItem(variantId, 1);
          refreshCartUi({ openDrawer: true });
        } catch (error) {
          console.error('Recommendation add to cart error:', error);
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  // ─── RECOMMENDATIONS SWIPER ─────────────────────────────────

  initRecommendationsSwiper() {
    if (typeof Swiper === 'undefined') return;

    const el = this.container.querySelector('[data-recommendations-swiper]');
    if (!el) return;

    new Swiper(el, {
      slidesPerView: 1.7,
      spaceBetween: 10,
      watchOverflow: true,
      breakpoints: {
        768: { slidesPerView: 3, spaceBetween: 12 },
      },
    });
  }

  // ─── PERFECT SET ────────────────────────────────────────────

  initPerfectSet() {
    const buyBtn = this.container.querySelector('[data-perfect-set-buy]');
    if (!buyBtn) return;

    buyBtn.addEventListener('click', async () => {
      const variant1 = Number(buyBtn.dataset.variant1);
      const variant2 = Number(buyBtn.dataset.variant2);
      if (!variant1 || !variant2) return;

      const initialHtml = buyBtn.innerHTML;
      buyBtn.disabled = true;
      buyBtn.innerHTML = 'Adding...';

      try {
        const payload = {
          items: [
            { id: variant1, quantity: 1 },
            { id: variant2, quantity: 1 },
          ],
          ...(glowBundledCartSectionPayload() || {}),
        };
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = await res.json();
          glowApplyBundledCartSections(data);
        }
        refreshCartUi({ openDrawer: true });
      } catch (error) {
        console.error('Perfect set add error:', error);
      } finally {
        buyBtn.disabled = false;
        buyBtn.innerHTML = initialHtml;
      }
    });
  }

  // ─── PDP SECTIONS (anchor nav + scroll-spy) ────────────────

  initPdpTabs() {
    const root = this.container.querySelector('[data-pdp-sections]');
    if (!root) return;

    const links = root.querySelectorAll('[data-pdp-nav-link]');
    const sections = root.querySelectorAll('[data-pdp-section]');

    if (!links.length || !sections.length) return;

    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').replace('#', '');
        const target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.dataset.pdpSection;
            links.forEach((l) => l.classList.remove('is-active'));
            const activeLink = root.querySelector(`[data-pdp-nav-link="${id}"]`);
            if (activeLink) activeLink.classList.add('is-active');
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
  }

  initSpecsShowMore() {
    this.container.querySelectorAll('[data-specs-table]').forEach((wrap) => {
      const btn = wrap.querySelector('[data-specs-more]');
      if (!btn) return;

      btn.addEventListener('click', () => {
        wrap.querySelectorAll('[data-specs-extra]').forEach((row) => {
          row.hidden = false;
        });
        btn.hidden = true;
      });
    });
  }

  initIncludedProductModal() {
    const popup = this.container.querySelector('[data-included-popup]');
    if (!popup) return;

    const dialog = popup.querySelector('[data-included-popup-dialog]');
    const panels = popup.querySelectorAll('[data-included-popup-panel]');
    const triggers = this.container.querySelectorAll('[data-included-open]');
    let lastTrigger = null;

    const closePopup = () => {
      if (popup.hidden) return;
      popup.hidden = true;
      document.body.style.overflow = '';
      if (lastTrigger) lastTrigger.focus();
    };

    const openPopup = (index) => {
      panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-included-popup-panel') === String(index);
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;

        if (isActive) {
          const title = panel.querySelector('[data-included-popup-title]');
          if (dialog && title && title.id) {
            dialog.setAttribute('aria-labelledby', title.id);
          }
          if (dialog) {
            dialog.classList.toggle(
              'complete-kit-popup__dialog--no-media',
              !panel.querySelector('.complete-kit-popup__media')
            );
          }
        }
      });

      popup.hidden = false;
      document.body.style.overflow = 'hidden';
      if (dialog) dialog.focus();
    };

    triggers.forEach((btn) => {
      btn.addEventListener('click', () => {
        lastTrigger = btn;
        openPopup(btn.getAttribute('data-included-open'));
      });
    });

    popup.querySelectorAll('[data-included-popup-close]').forEach((el) => {
      el.addEventListener('click', closePopup);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePopup();
    });

    if (dialog) dialog.setAttribute('tabindex', '-1');
  }

  // ─── SIDEBAR ADD TO CART ───────────────────────────────────

  initSidebarAtc() {
    const btn = this.container.querySelector('[data-pdp-sidebar-atc]');
    if (!btn) return;

    if (this.currentVariant) this.syncAtcNotifyState(btn, this.currentVariant);

    btn.addEventListener('click', async () => {
      if (btn.classList.contains('klaviyo-bis-trigger')) return;

      const variantId = Number(btn.dataset.variantId);
      if (!variantId) return;

      const textEl = btn.querySelector('[data-sidebar-atc-text]');
      const iconEl = btn.querySelector('[data-sidebar-atc-icon]');
      const initialText = textEl ? textEl.textContent : 'Add to cart';
      btn.disabled = true;
      if (textEl) textEl.textContent = 'Adding...';
      if (iconEl) iconEl.hidden = true;

      try {
        await addCartItem(variantId, 1);
        refreshCartUi({ openDrawer: true });
      } catch (error) {
        console.error('Sidebar add to cart error:', error);
      } finally {
        btn.disabled = false;
        if (textEl) textEl.textContent = initialText;
        if (iconEl) iconEl.hidden = false;
      }
    });
  }

  // ─── VARIANT SELECTOR ────────────────────────────────────────

  initVariantSelector() {
    const groups = this.container.querySelectorAll('[data-variant-option-group]');
    if (!groups.length || !this.variantData.length) return;

    this.selectedOptions = [];
    groups.forEach((group) => {
      const active = group.querySelector('.product-page__variant-btn.is-active');
      this.selectedOptions.push(active ? active.dataset.variantValue : '');
    });

    groups.forEach((group) => {
      const btns = group.querySelectorAll('.product-page__variant-btn');
      btns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = Number(group.dataset.optionIndex);
          this.selectedOptions[idx] = btn.dataset.variantValue;

          group.querySelectorAll('.product-page__variant-btn').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          this.updateVariantFromOptions();
        });
      });
    });
  }

  updateVariantFromOptions() {
    const matched = this.variantData.find((v) => {
      return v.options.every((opt, i) => opt === this.selectedOptions[i]);
    });
    if (!matched) return;

    this.currentVariant = matched;

    const variantInput = this.container.querySelector('[data-variant-id]');
    if (variantInput) variantInput.value = matched.id;

    const addBtn = this.container.querySelector('[data-add-to-cart]') || this.addToCartBtn;
    if (addBtn) {
      this.syncAtcNotifyState(addBtn, matched);
    }

    const sidebarBtn = this.container.querySelector('[data-pdp-sidebar-atc]');
    if (sidebarBtn) {
      sidebarBtn.dataset.variantId = matched.id;
      this.syncAtcNotifyState(sidebarBtn, matched);
    }

    this.refreshSellingPlanOptionPrices(matched.price);
    this.updateAtcPrice();

    const comparePriceEl = this.container.querySelector('[data-compare-price]');
    if (comparePriceEl) {
      if (matched.compare_at_price && matched.compare_at_price > matched.price) {
        comparePriceEl.textContent = this.formatMoney(matched.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    const oneTimePriceEl = this.container.querySelector('[data-purchase-card="onetime"] .product-page__purchase-price-value');
    if (oneTimePriceEl) oneTimePriceEl.textContent = this.formatMoney(matched.price);

    const stockEl = this.container.querySelector('[data-product-stock]');
    if (stockEl) {
      if (matched.available) {
        stockEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="16" height="16" rx="8" fill="#27A431"/><path d="M5 8.4L6.71429 10L11 6" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="product-page__stock-text">In stock</span>';
      } else {
        stockEl.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#ef4444"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/></svg><span>Out of stock</span>';
      }
    }

    if (matched.featured_media) {
      this.slideToMedia(matched.featured_media.id);
    } else if (matched.featured_image && matched.featured_image.position != null) {
      const pos = matched.featured_image.position - 1;
      if (this.mainSwiper && pos >= 0) {
        this.mainSwiper.slideTo(pos, 300);
      }
    }

    const stickyBar = document.querySelector('[data-pdp-sticky-bar]');
    if (stickyBar) {
      const stickyCompare = stickyBar.querySelector('[data-sticky-compare]');
      const stickyBtn = stickyBar.querySelector('[data-sticky-bar-atc]');
      if (stickyCompare) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          stickyCompare.textContent = this.formatMoney(matched.compare_at_price);
          stickyCompare.style.display = '';
        } else {
          stickyCompare.style.display = 'none';
        }
      }
      if (stickyBtn) {
        stickyBtn.dataset.variantId = matched.id;
        this.syncAtcNotifyState(stickyBtn, matched);
      }
    }

    const url = new URL(window.location);
    url.searchParams.set('variant', matched.id);
    window.history.replaceState({}, '', url.toString());
  }

  refreshSellingPlanOptionPrices(basePrice) {
    const select = this.container.querySelector('[data-selling-plan-select]');
    if (!select?.options) return;

    Array.from(select.options).forEach((option) => {
      const adjusted = this.getPlanAdjustedPrice(basePrice, option);
      if (adjusted != null) option.dataset.planPrice = String(adjusted);
    });

    const activeCard = this.container.querySelector('[data-purchase-card].is-active');
    const isSubscribe = activeCard && activeCard.dataset.purchaseCard === 'subscribe';
    const subPriceEl = this.container.querySelector('[data-sub-price]');
    const badgeEl = this.container.querySelector('.product-page__purchase-badge');
    const selectedOption = select.options[select.selectedIndex];

    if (isSubscribe && subPriceEl && selectedOption) {
      const planPrice = Number(selectedOption.dataset.planPrice);
      if (planPrice > 0) subPriceEl.textContent = this.formatMoney(planPrice);
    } else if (subPriceEl && selectedOption) {
      const planPrice = Number(selectedOption.dataset.planPrice);
      if (planPrice > 0) subPriceEl.textContent = this.formatMoney(planPrice);
    }

    if (badgeEl && selectedOption?.dataset.planDiscount) {
      badgeEl.textContent = `Save ${selectedOption.dataset.planDiscount}%`;
    }

    const compareEl = this.container.querySelector('.product-page__purchase-price-compare');
    if (compareEl) {
      compareEl.textContent = this.formatMoney(basePrice);
      compareEl.style.display = Number(selectedOption?.dataset.planPrice) < basePrice ? '' : 'none';
    }
  }

  // ─── STICKY BOTTOM BAR ─────────────────────────────────────

  initStickyBar() {
    const bar = document.querySelector('[data-pdp-sticky-bar]');
    if (!bar) return;

    const heroSection = this.container.querySelector('.product-page__info');
    if (!heroSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            bar.hidden = true;
          } else {
            // Hide sticky bar entirely when current variant is OOS
            bar.hidden = bar.dataset.forceHidden === 'true';
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(heroSection);

    const atcBtn = bar.querySelector('[data-sticky-bar-atc]');
    if (atcBtn) {
      if (this.currentVariant) this.syncAtcNotifyState(atcBtn, this.currentVariant);

      atcBtn.addEventListener('click', async () => {
        if (bar.dataset.forceHidden === 'true') return;

        const variantId = Number(atcBtn.dataset.variantId);
        if (!variantId) return;

        const textEl = atcBtn.querySelector('[data-sticky-atc-text]');
        const iconEl = atcBtn.querySelector('[data-sticky-atc-icon]');
        const initialText = textEl ? textEl.textContent : 'Add to cart';
        atcBtn.disabled = true;
        if (textEl) textEl.textContent = 'Adding...';
        if (iconEl) iconEl.hidden = true;

        try {
          await addCartItem(variantId, 1);
          refreshCartUi({ openDrawer: true });
        } catch (error) {
          console.error('Sticky bar add to cart error:', error);
        } finally {
          atcBtn.disabled = false;
          if (textEl) textEl.textContent = initialText;
          if (iconEl) iconEl.hidden = false;
        }
      });
    }
  }

  // ─── PRODUCT FORM (AJAX) ───────────────────────────────────

  initProductForm() {
    const form = this.container.querySelector('[data-product-form]');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const submitBtn = form.querySelector('[data-add-to-cart]');
      if (submitBtn?.classList.contains('klaviyo-bis-trigger')) return;

      const variantInput = form.querySelector('[data-variant-id]');
      const quantityInput = form.querySelector('[data-quantity-input]');
      const sellingPlanInput = form.querySelector('[data-selling-plan-input]');

      const variantId = Number(variantInput?.value);
      const quantity = Number(quantityInput?.value || 1);
      const sellingPlanId = sellingPlanInput?.value || '';

      if (!variantId) return;

      const textEl = submitBtn?.querySelector('[data-add-to-cart-text]');
      const originalLabel = textEl
        ? textEl.childNodes[0]?.textContent?.trim() || 'Add to cart'
        : 'Add to cart';

      try {
        if (submitBtn) submitBtn.disabled = true;
        if (textEl) textEl.textContent = 'Adding...';

        if (sellingPlanId) {
          await addCartItemWithSellingPlan(variantId, quantity > 0 ? quantity : 1, sellingPlanId);
        } else {
          await addCartItem(variantId, quantity > 0 ? quantity : 1);
        }

        const subCheckbox = this.container.querySelector('[data-subscription-checkbox]');
        if (subCheckbox && subCheckbox.checked) {
          const subVariantId = Number(subCheckbox.dataset.subVariantId);
          const subSellingPlan = subCheckbox.dataset.subSellingPlan || '';
          if (subVariantId) {
            try {
              await addCartItemWithSellingPlan(subVariantId, 1, subSellingPlan);
            } catch (subError) {
              // non-critical: subscription product add failed
            }
          }
        }

        refreshCartUi({ openDrawer: true });
      } catch (error) {
        refreshCartUi({ openDrawer: true });
      } finally {
        if (submitBtn && this.currentVariant) {
          this.syncAtcNotifyState(submitBtn, this.currentVariant);
        }
        if (textEl && this.currentVariant?.available) {
          textEl.innerHTML = originalLabel + ' <span data-atc-price></span>';
          this.updateAtcPrice();
        }
      }
    });
  }

  syncAtcNotifyState(addBtn, variant) {
    if (!addBtn || !variant) return;

    addBtn.dataset.variantId = String(variant.id);
    addBtn.dataset.unitPrice = String(variant.price);
    addBtn.disabled = false;

    const cartIcon = addBtn.querySelector('[data-atc-cart-icon], [data-sticky-atc-icon], [data-sidebar-atc-icon]');
    const textEl = addBtn.querySelector('[data-add-to-cart-text], [data-sticky-atc-text], [data-sidebar-atc-text]');
    const isMainAtc = addBtn.hasAttribute('data-add-to-cart');
    const isSticky = addBtn.hasAttribute('data-sticky-bar-atc');

    if (isSticky) {
      const bar = addBtn.closest('[data-pdp-sticky-bar]');
      if (bar) {
        bar.dataset.forceHidden = variant.available ? 'false' : 'true';
        if (!variant.available) bar.hidden = true;
      }
    }

    if (variant.available) {
      if (isMainAtc) addBtn.type = 'submit';
      addBtn.classList.remove('product-page__add-to-cart--notify', 'klaviyo-bis-trigger');
      addBtn.removeAttribute('data-klaviyo-bis-trigger');
      if (cartIcon) cartIcon.hidden = false;
      if (textEl) {
        if (isMainAtc) {
          textEl.innerHTML = 'Add to cart <span data-atc-price></span>';
        } else {
          textEl.textContent = 'Add to cart';
        }
      }
      if (isMainAtc) this.updateAtcPrice();
      return;
    }

    // Sticky bar is hidden when OOS — don't turn it into a notify trigger
    if (isSticky) return;

    if (isMainAtc) addBtn.type = 'button';
    addBtn.classList.add('product-page__add-to-cart--notify', 'klaviyo-bis-trigger');
    addBtn.setAttribute('data-klaviyo-bis-trigger', '');
    if (cartIcon) cartIcon.hidden = true;
    if (textEl) textEl.textContent = 'Notify Me When Available';
  }

  // ─── SELLING PLAN WIDGET ─────────────────────────────────────

  initSellingPlanWidget() {
    const widget = this.container.querySelector('[data-selling-plan-widget]');
    if (!widget) return;

    const cards = widget.querySelectorAll('[data-purchase-card]');
    const select = widget.querySelector('[data-selling-plan-select]');
    const hiddenInput = this.container.querySelector('[data-selling-plan-input]');
    const subPriceEl = widget.querySelector('[data-sub-price]');
    const badgeEl = widget.querySelector('.product-page__purchase-badge');

    if (!cards.length || !hiddenInput) return;

    const formatMoney = (cents) => {
      var currency = window.Shopify?.currency?.active || 'AUD';
      var locale = window.Shopify?.locale || 'en-AU';
      var formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      }).format((cents || 0) / 100);
      return formatted.replace('A$', '$').replace('AU$', '$');
    };

    const syncSellingPlan = () => {
      const activeCard = widget.querySelector('[data-purchase-card].is-active');
      const isSubscribe = activeCard && activeCard.dataset.purchaseCard === 'subscribe';
      hiddenInput.value = isSubscribe && select ? select.value || '' : '';
      this.updateAtcPrice();
    };

    const updateSubPrice = () => {
      if (!select || select.tagName !== 'SELECT') return;
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const basePrice = this.currentVariant?.price ?? 0;
      const planPrice = this.getPlanAdjustedPrice(basePrice, selectedOption);
      const planDiscount = selectedOption.dataset.planDiscount;

      if (planPrice != null) {
        selectedOption.dataset.planPrice = String(planPrice);
      }

      if (subPriceEl && planPrice != null) {
        subPriceEl.textContent = formatMoney(planPrice);
      }
      if (badgeEl && planDiscount) {
        badgeEl.textContent = `Save ${planDiscount}%`;
      }
      this.updateAtcPrice();
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        cards.forEach((c) => c.classList.remove('is-active'));
        card.classList.add('is-active');
        syncSellingPlan();
      });
    });

    if (select && select.tagName === 'SELECT') {
      select.addEventListener('change', () => {
        syncSellingPlan();
        updateSubPrice();
      });
    }

    syncSellingPlan();
  }

  // ─── HELPERS ──────────────────────────────────────────────────

  formatMoney(cents) {
    var currency = window.Shopify?.currency?.active || 'AUD';
    var locale = window.Shopify?.locale || 'en-AU';
    var formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format((cents || 0) / 100);
    return formatted.replace('A$', '$').replace('AU$', '$');
  }
}

const initProductPages = (scope = document) => {
  const productSections = scope.querySelectorAll('[data-product-page]');
  productSections.forEach((section) => {
    new ProductPage(section);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initProductPages();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductPages(event.target);
});

document.addEventListener('shopify:section:reorder', () => {
  initProductPages();
});




function formatMoneyGBP(cents) {
  var currency = window.Shopify?.currency?.active || 'AUD';
  var locale = window.Shopify?.locale || 'en-AU';
  return (cents / 100).toLocaleString(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  });
}

// UPSELL

function parseUpsellVariantIds(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter(Boolean);
}

function glowBundledCartSectionPayload() {
  const ids = [];
  const drawer = document.querySelector('[data-cart-drawer-root]');
  if (drawer?.dataset.sectionId) {
    ids.push(drawer.dataset.sectionId);
  }

  const cartRoot = document.querySelector('[data-main-cart-root]');
  if (cartRoot?.dataset.sectionId) {
    ids.push(cartRoot.dataset.sectionId);
  }

  if (!ids.length) {
    return null;
  }

  const cartPath = window.__glowRoutes?.cartUrl || '/cart';
  const sections_url = cartPath.startsWith('/') ? cartPath : `/${cartPath}`;

  return {
    sections: ids.join(','),
    sections_url,
  };
}

function glowApplyBundledCartSections(result) {
  if (result?.sections && window.cartUi?.applyBundledSections) {
    window.cartUi.applyBundledSections(result.sections);
  }
}

async function getCartData() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to load cart');
  }

  return response.json();
}

async function addCartItem(variantId, quantity = 1, properties = {}) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: Number(variantId),
      quantity,
      properties,
      ...(glowBundledCartSectionPayload() || {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add item to cart');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function addCartItemWithSellingPlan(variantId, quantity = 1, sellingPlan = '') {
  const body = {
    id: Number(variantId),
    quantity,
    ...(glowBundledCartSectionPayload() || {}),
  };
  if (sellingPlan) {
    body.selling_plan = sellingPlan.toString().includes('gid://') ? sellingPlan : Number(sellingPlan);
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.description || 'Failed to add subscription item to cart');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function updateCartItemQuantity(lineKey, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      ...(glowBundledCartSectionPayload() || {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update cart item quantity');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function addUpsellGiftIfNeeded(section) {
  if (!section) return false;

  const productsWrap = section.querySelector('.product-upsell__products');
  if (!productsWrap) return false;

  const requiredVariantIds = parseUpsellVariantIds(productsWrap.dataset.requiredVariants);
  const giftVariantId = Number(section.dataset.giftVariantId);

  if (!requiredVariantIds.length || !giftVariantId) {
    return false;
  }

  const cart = await getCartData();
  const cartVariantIds = cart.items.map((item) => Number(item.variant_id));

  const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
    cartVariantIds.includes(variantId)
  );

  const hasGiftAlready = cartVariantIds.includes(giftVariantId);

  if (!hasAllRequiredProducts || hasGiftAlready) {
    return false;
  }

  await addCartItem(giftVariantId, 1, {
    upsell_gift: 'true',
    upsell_required_variant_ids: requiredVariantIds.join(','),
  });

  return true;
}

function refreshCartUi(detail = {}) {
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
}


async function removeInvalidUpsellGiftsIfNeeded() {
  const cart = await getCartData();
  const cartVariantIds = new Set(cart.items.map((item) => Number(item.variant_id)));
  const giftLineKeysToRemove = [];

  cart.items.forEach((item) => {
    const properties = item.properties || {};
    const isUpsellGift =
      String(properties.upsell_gift).toLowerCase() === 'true'
      || String(properties._upsell_gift).toLowerCase() === 'true';
    if (!isUpsellGift) return;

    const requiredVariantIds = parseUpsellVariantIds(
      properties.upsell_required_variant_ids || properties._upsell_required_variant_ids
    );
    if (!requiredVariantIds.length) return;

    const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
      cartVariantIds.has(variantId)
    );

    if (!hasAllRequiredProducts) {
      giftLineKeysToRemove.push(item.key);
    }
  });

  if (!giftLineKeysToRemove.length) return false;

  await Promise.all(giftLineKeysToRemove.map((lineKey) => updateCartItemQuantity(lineKey, 0)));
  return true;
}

let isUpsellGiftSyncInProgress = false;

async function syncUpsellGiftState() {
  if (isUpsellGiftSyncInProgress) return;

  isUpsellGiftSyncInProgress = true;
  try {
    const wasGiftRemoved = await removeInvalidUpsellGiftsIfNeeded();
    if (wasGiftRemoved) {
      refreshCartUi();
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    }
  } catch (error) {
    console.error('Upsell gift sync error:', error);
  } finally {
    isUpsellGiftSyncInProgress = false;
  }
}

function setAjaxButtonLoadingState(button, loading, loadingText = 'Adding to cart...') {
  if (!button) return;

  if (!button.dataset.initialText) {
    button.dataset.initialText = button.textContent.trim();
  }

  if (loading) {
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.textContent = loadingText;
  } else {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
    button.textContent = button.dataset.initialText;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('.product-upsell .product-upsell__card-button');
  if (!button) return;

  event.preventDefault();

  const section = button.closest('.product-upsell');
  const card = button.closest('.product-upsell__card');
  const variantId = Number(button.dataset.variantId || card?.dataset.variantId);

  if (!section || !variantId) return;

  try {
    setAjaxButtonLoadingState(button, true);

    await addCartItem(variantId);

    await addUpsellGiftIfNeeded(section);

    refreshCartUi({ openDrawer: true });
  } catch (error) {
    console.error('Upsell add to cart error:', error);
  } finally {
    setAjaxButtonLoadingState(button, false);
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-ajax-add-to-cart-form]');
  if (!form) return;

  event.preventDefault();

  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const submitButton = form.querySelector('[data-ajax-add-to-cart-button]');
  const variantId = Number(variantInput?.value);
  const quantity = Number(quantityInput?.value || 1);

  if (!variantId) return;

  try {
    setAjaxButtonLoadingState(submitButton, true);
    await addCartItem(variantId, quantity > 0 ? quantity : 1);
    refreshCartUi({ openDrawer: true });
  } catch (error) {
    console.error('Add to cart form error:', error);
  } finally {
    setAjaxButtonLoadingState(submitButton, false);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  syncUpsellGiftState();
});

document.addEventListener('cart:refresh', () => {
  syncUpsellGiftState();
});


// ACCORDION

function initProductAccordion(scope = document) {
  const accordions = scope.querySelectorAll('[data-product-accordion]');

  accordions.forEach((accordion) => {
    if (accordion.dataset.accordionInitialized === 'true') return;
    accordion.dataset.accordionInitialized = 'true';

    const items = accordion.querySelectorAll('.product-accordion__item');

    let hasOpenedItem = false;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const panel = item.querySelector('[data-accordion-panel]');

      if (!trigger || !panel) return;

      panel.hidden = false;
      panel.style.overflow = 'hidden';

      if (!isMobile && item.classList.contains('is-open') && !hasOpenedItem) {
        hasOpenedItem = true;
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = 'auto';
      } else {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = '0px';
      }
    });

    accordion.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const item = trigger.closest('.product-accordion__item');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');

      items.forEach((accordionItem) => {
        if (accordionItem !== item && accordionItem.classList.contains('is-open')) {
          closeAccordionItem(accordionItem);
        }
      });

      if (isOpen) {
        closeAccordionItem(item);
      } else {
        openAccordionItem(item);
      }
    });
  });
}

function openAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  item.classList.add('is-open');
  trigger.setAttribute('aria-expanded', 'true');

  panel.style.height = `${panel.scrollHeight}px`;

  const onTransitionEnd = (event) => {
    if (event.propertyName !== 'height') return;

    if (item.classList.contains('is-open')) {
      panel.style.height = 'auto';
    }

    panel.removeEventListener('transitionend', onTransitionEnd);
  };

  panel.addEventListener('transitionend', onTransitionEnd);
}

function closeAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  panel.style.height = `${panel.scrollHeight}px`;
  panel.offsetHeight;

  item.classList.remove('is-open');
  trigger.setAttribute('aria-expanded', 'false');

  requestAnimationFrame(() => {
    panel.style.height = '0px';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductAccordion();
  initFaqFromRichtext();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductAccordion(event.target);
  initFaqFromRichtext(event.target);
});

function initFaqFromRichtext(scope = document) {
  const containers = scope.querySelectorAll('[data-faq-richtext]');
  containers.forEach((container) => {
    if (container.dataset.faqInit === 'true') return;
    container.dataset.faqInit = 'true';

    const source = container.querySelector('[data-faq-source]');
    if (!source) return;

    const paragraphs = source.querySelectorAll('p');
    if (!paragraphs.length) return;

    const items = [];
    paragraphs.forEach((p) => {
      const strong = p.querySelector('strong');
      if (!strong) return;
      const question = strong.textContent.trim().replace(/^\d+[\.\)]\s*/, '');
      const clone = p.cloneNode(true);
      const strongClone = clone.querySelector('strong');
      if (strongClone) strongClone.remove();
      const answer = clone.innerHTML.replace(/^[\s<br>\n]+/, '').trim();
      if (question) items.push({ question, answer });
    });

    if (!items.length) return;

    const list = document.createElement('div');
    list.className = 'pdp-sections__faq-list';

    items.forEach((item, idx) => {
      const details = document.createElement('details');
      details.className = 'pdp-sections__faq-item';
      if (idx === 0 && window.matchMedia('(min-width: 769px)').matches) {
        details.open = true;
      }

      details.innerHTML =
        '<summary class="pdp-sections__faq-trigger">' +
          '<span class="pdp-sections__faq-question">' + item.question + '</span>' +
          '<span class="pdp-sections__faq-arrow">' +
            '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="28" height="28" rx="14" fill="currentColor" fill-opacity="0.1"/><path d="M12.1387 20.1641L18.0938 14.209L12.1387 8.25385" stroke="currentColor" stroke-width="1.70146" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</span>' +
        '</summary>' +
        '<div class="pdp-sections__faq-answer">' + item.answer + '</div>';

      list.appendChild(details);
    });

    source.remove();
    container.appendChild(list);
  });
}

